import {
  Category,
  HeroBenefit,
  Periodicity,
  Plan,
  PlanFilters,
  PriceRange,
  ProductType,
  PromoCard,
  SortOption,
} from "../types";

export const HERO = {
  badge: "Seu futuro começa com prática",
  title: "Escolha o plano ideal para você",
  subtitle: "Questões, simulados e estatísticas para diferentes concursos e objetivos.",
};

export const HERO_BENEFITS: HeroBenefit[] = [
  { icon: "check", label: "Estude com questões atualizadas" },
  { icon: "bar-chart", label: "Acompanhe sua evolução" },
  { icon: "trophy", label: "Aumente suas chances de aprovação" },
];

export const CATEGORIES: Category[] = [
  { id: "all", label: "Todos", icon: "grid", featured: true, featuredMobile: true },
  { id: "choa-tap", label: "CHOA/TAP", icon: "flame", featured: true, featuredMobile: true },
  { id: "fiscal", label: "Área Fiscal", icon: "landmark", featured: true },
  { id: "seguranca-publica", label: "Segurança Pública", icon: "shield", featured: true, featuredMobile: true },
  { id: "educacao", label: "Educação", icon: "book-open", featured: true },
  { id: "juridico", label: "Jurídico", icon: "scale", featured: true },
  { id: "saude", label: "Saúde", icon: "heart", featured: true },
  { id: "administrativo", label: "Administrativo", icon: "users", featured: true },
  { id: "ti", label: "TI", icon: "monitor", featured: true },
  { id: "bancario", label: "Bancário", icon: "building", featured: false },
  { id: "tribunais", label: "Tribunais", icon: "gavel", featured: false },
];

export const PRODUCT_TYPES: ProductType[] = [
  { id: "assinatura", label: "Planos de assinatura" },
  { id: "simulado", label: "Simulados avulsos" },
  { id: "flash-cards", label: "Flash Cards" },
];

export const PERIODICITIES: Periodicity[] = [
  { id: "mensal", label: "Mensal", priceSuffix: "/mês", days: 30 },
  { id: "trimestral", label: "Trimestral", priceSuffix: "/trimestre", days: 90 },
  { id: "semestral", label: "Semestral", priceSuffix: "/semestre", days: 180 },
  { id: "anual", label: "Anual", priceSuffix: "/ano", days: 365 },
];

export const SORT_OPTIONS: SortOption[] = [
  { id: "popular", label: "Mais populares" },
  { id: "price-asc", label: "Menor preço" },
  { id: "price-desc", label: "Maior preço" },
  { id: "recent", label: "Mais recentes" },
];

export const PRICE_RANGE: PriceRange & { step: number } = { min: 0, max: 1000, step: 10 };

export const DEFAULT_FILTERS: PlanFilters = {
  categoryId: "all",
  search: "",
  typeIds: [],
  periodicityIds: [],
  price: { min: PRICE_RANGE.min, max: PRICE_RANGE.max },
  sort: "popular",
};

export const PLANS: Plan[] = [
  {
    id: "cadete-cbmgo",
    name: "Cadete Aluno Oficial (CBMGO)",
    slug: "cadete-aluno-oficial-cbmgo",
    categoryId: "seguranca-publica",
    typeId: "assinatura",
    periodicityId: "mensal",
    description: "Domine a legislação específica do CBM-GO resolvendo questões elaboradas para o concurso de Cadete.",
    price: 29.9,
    benefits: ["Estatísticas em tempo real", "Questões que já caíram", "Questões inéditas"],
    icon: "award",
    accent: "orange",
    createdAt: "2025-06-01",
  },
  {
    id: "soldado-cbmgo",
    name: "Soldado Combatente (CBMGO)",
    slug: "soldado-combatente-cbmgo",
    categoryId: "seguranca-publica",
    typeId: "assinatura",
    periodicityId: "mensal",
    description: "Domine a legislação específica do CBM-GO resolvendo questões elaboradas para o concurso de Soldado.",
    price: 29.9,
    benefits: ["Estatísticas em tempo real", "Questões que já caíram", "Questões inéditas"],
    icon: "flame",
    accent: "orange",
    createdAt: "2025-06-01",
  },
  {
    id: "cadete-pmgo",
    name: "Cadete Aluno Oficial (PMGO)",
    slug: "cadete-aluno-oficial-pmgo",
    categoryId: "seguranca-publica",
    typeId: "assinatura",
    periodicityId: "mensal",
    description: "Resolva questões do concurso de Cadete.",
    price: 29.9,
    benefits: ["Estatísticas em tempo real", "Questões que já caíram"],
    icon: "award",
    accent: "blue",
    createdAt: "2025-06-01",
  },
  {
    id: "soldado-pmgo",
    name: "Soldado Combatente (PMGO)",
    slug: "soldado-combatente-pmgo",
    categoryId: "seguranca-publica",
    typeId: "assinatura",
    periodicityId: "mensal",
    description: "Resolva questões que já caíram para este concurso.",
    price: 29.90,
    benefits: ["Estatísticas em tempo real", "Questões que já caíram"],
    icon: "shield",
    accent: "blue",
    createdAt: "2025-06-01",
  },

  {
    id: "plano-tap",
    name: "Plano TAP",
    slug: "plano-tap",
    categoryId: "choa-tap",
    typeId: "assinatura",
    periodicityId: "trimestral",
    description: "Acesso completo ao módulo TAP com questões, simulados e estatísticas.",
    longDescription:
      "Trimestre completo de preparação para o TAP: banco de questões atualizado, simulados inéditos com correção imediata e painel de estatísticas que mostra onde você mais erra — tudo em um só lugar.",
    price: 599.9,
    installment: { count: 12, amount: 59.9 },
    benefits: [
      "Todas as questões do TAP",
      "Simulados inéditos",
      "Estatísticas em tempo real",
      "Flash Cards",
      "Suporte prioritário",
    ],
    icon: "shield-check",
    accent: "green",
    popular: true,
    createdAt: "2026-01-15",
  },
  {
    id: "combo-choa",
    name: "Combo CHOA",
    slug: "combo-choa",
    categoryId: "choa-tap",
    typeId: "assinatura",
    periodicityId: "trimestral",
    description: "Tudo que você precisa para maximizar suas chances no CHOA.",
    longDescription:
      "O pacote mais completo para o CHOA: módulo integral, simulados síncronos no mesmo horário para toda a turma, estatísticas de desempenho por matéria e revisão espaçada para fixar o conteúdo.",
    price: 799.9,
    installment: { count: 12, amount: 79.9 },
    benefits: [
      "Módulo completo CHOA",
      "Simulados síncronos",
      "Estatísticas de desempenho",
      "Flash Cards exclusivos",
      "Revisão espaçada",
    ],
    icon: "star",
    accent: "blue",
    createdAt: "2026-02-10",
  },
  {
    id: "choa-mensal",
    name: "CHOA Mensal",
    slug: "choa-mensal",
    categoryId: "choa-tap",
    typeId: "assinatura",
    periodicityId: "mensal",
    description: "Acesso mensal com foco no CHOA. Ideal para quem quer praticidade.",
    price: 299.9,
    installment: { count: 12, amount: 29.9 },
    benefits: [
      "Questões atualizadas",
      "Filtro por assunto",
      "Estatísticas em tempo real",
      "Índice de acertos",
      "Flash Cards",
    ],
    icon: "bar-chart",
    accent: "purple",
    createdAt: "2026-03-05",
  },
  {
    id: "tap-mensal",
    name: "TAP Mensal",
    slug: "tap-mensal",
    categoryId: "choa-tap",
    typeId: "assinatura",
    periodicityId: "mensal",
    description: "Estude no seu ritmo com acesso mensal ao TAP.",
    price: 249.9,
    installment: { count: 12, amount: 24.9 },
    benefits: [
      "Questões do TAP",
      "Filtro por assunto",
      "Estatísticas em tempo real",
      "Simulados inclusos",
      "Flash Cards",
    ],
    icon: "calendar",
    accent: "orange",
    createdAt: "2026-03-20",
  },
];

export const PROMO_CARDS: PromoCard[] = [
  {
    id: "estudantes",
    title: "Planos para Estudantes",
    description: "Descontos especiais para quem está começando.",
    linkLabel: "Ver planos",
    href: "/contato?assunto=estudantes",
    icon: "graduation-cap",
    accent: "blue",
  },
  {
    id: "grupos",
    title: "Planos para Grupos",
    description: "Condições especiais para turmas e grupos de estudo.",
    linkLabel: "Ver opções",
    href: "/contato?assunto=grupos",
    icon: "users",
    accent: "purple",
  },
  {
    id: "avulsos",
    title: "Produtos Avulsos",
    description: "Simulados, pacotes de questões e muito mais.",
    linkLabel: "Ver produtos",
    href: "/contato?assunto=avulsos",
    icon: "gift",
    accent: "cyan",
  },
];
